import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import useMenuBack from '../../hooks/useMenuBack'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Bell, Calendar, FileText, Eye, CheckCircle, Send, User, RefreshCw, UserPlus, Clock, XCircle, Settings, MessageSquare, Stethoscope, Trash2, AlertTriangle, UserX } from 'lucide-react'
import { notificationsAPI } from '../../services/api'
import { formatTimeAgo, formatDate, formatAppointmentTime } from '../../utils/dateUtils'
import { useNotificationStore, useCaseStore } from '../../stores'
import WhatsNewNotice from '../../components/WhatsNewNotice'
import { useI18n } from '../../i18n'
import { isCaseDeclined, isCaseDeclineUndone, caseDeclinedCopy } from '../../utils/caseDeclined'
import { physicianCancelNotice, cancelReasonKey } from '../../utils/declinedRequest' // PROD-1137
import { clinicianFormsFor } from '../../utils/clinicianName.js'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * NotificationsScreen v220.12
 *
 * Sleek redesign — row-based, time-grouped, chevron-as-CTA.
 *
 * Changes from v179.0.0:
 *  - Cards are rows with hairline dividers — no per-card boxes/borders
 *  - Time-grouped: Today / This week / Earlier (was: New / Earlier)
 *  - Unread state: subtle ice-blue tint + small blue dot at right edge
 *  - Read state: opacity 0.85 + lighter title weight, same layout
 *  - All icons from brand palette (Navy/Blue/Light Blue/neutral) — no green or amber
 *  - case_message uses MessageSquare icon (was Stethoscope)
 *  - Single-line titles for short events; multi-line only when there's detail
 *  - Universal chevron CTA on every row (replaces the per-card icon-badge-only pattern)
 *  - Removed legacy purple avs_ready style
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'

// Notification type → icon + color mapping — all brand-palette
// v220.12.2: Added legacy dot-notation aliases (appointment.confirmed, etc.) for old rows
// + observation_finding_reported (was falling through to default Bell)
const NOTIF_STYLES = {
  // Care updates
  case_finalized:         { Icon: CheckCircle, bg: ICE_BLUE,                   color: NAVY },
  case_accepted:          { Icon: CheckCircle, bg: 'rgba(70,168,249,0.12)',   color: BLUE },
  case_message:           { Icon: MessageSquare, bg: 'rgba(70,168,249,0.12)', color: BLUE },
  case_message_to_doctor: { Icon: MessageSquare, bg: 'rgba(70,168,249,0.12)', color: BLUE },
  follow_up_ready:        { Icon: FileText,    bg: ICE_BLUE,                   color: NAVY },
  referral_created:       { Icon: UserPlus,    bg: ICE_BLUE,                   color: BLUE },
  // TEST 3.18 (patient 242.231.0): the physician the case was sent to declined it.
  case_declined:          { Icon: UserX,       bg: 'rgba(239,68,68,0.08)',    color: '#EF4444' },
  // Appointments — current snake_case
  appointment_confirmed:  { Icon: Calendar,    bg: 'rgba(70,168,249,0.12)',   color: BLUE },
  // s111: the patient's list of possible conditions was re-ranked from their records.
  case_summary_updated:   { Icon: RefreshCw,   bg: '#F2FAF3',                  color: '#2F7A38' },
  appointment_modified:   { Icon: RefreshCw,   bg: 'rgba(70,168,249,0.12)',   color: BLUE },
  appointment_rejected:   { Icon: XCircle,     bg: 'rgba(239,68,68,0.08)',    color: '#EF4444' },
  appointment_cancelled:  { Icon: XCircle,     bg: '#F1F5F9',                  color: '#64748B' },
  // PROD-1137: the physician (or his MA) cancelled the patient's confirmed visit.
  appointment_cancelled_by_doctor: { Icon: XCircle, bg: 'rgba(239,68,68,0.08)', color: '#EF4444' },
  appointment_request:    { Icon: Calendar,    bg: 'rgba(70,168,249,0.12)',   color: BLUE },
  // Appointments — legacy dot-notation (pre-unified notify_user, rows from Mar 2026 and earlier)
  'appointment.confirmed':   { Icon: Calendar,  bg: 'rgba(70,168,249,0.12)',  color: BLUE },
  'appointment.rescheduled': { Icon: RefreshCw, bg: 'rgba(70,168,249,0.12)',  color: BLUE },
  'appointment.rejected':    { Icon: XCircle,   bg: 'rgba(239,68,68,0.08)',   color: '#EF4444' },
  'appointment.cancelled':   { Icon: XCircle,   bg: '#F1F5F9',                 color: '#64748B' },
  // Monitoring
  observation_check_in:        { Icon: Clock,   bg: 'rgba(70,168,249,0.12)',  color: BLUE },
  observation_finding_reported:{ Icon: Clock,   bg: 'rgba(70,168,249,0.12)',  color: BLUE },
  // Room ready (live appointment)
  room_ready:             { Icon: Bell,        bg: 'rgba(102,209,112,0.18)',  color: '#2F8C3A' },
  // Case activity
  case_sent:              { Icon: Send,        bg: '#F1F5F9',                  color: '#64748B' },
  case_shared:            { Icon: Send,        bg: '#F1F5F9',                  color: '#64748B' },
  case_viewed:            { Icon: Eye,         bg: '#F1F5F9',                  color: '#64748B' },
  case_reopened:          { Icon: RefreshCw,   bg: '#F1F5F9',                  color: '#64748B' },
  // Account
  welcome:                { Icon: CheckCircle, bg: ICE_BLUE,                   color: BLUE },
  profile_updated:        { Icon: User,        bg: '#F1F5F9',                  color: '#64748B' },
  // Doctor-facing (when patient views as their own activity)
  'patient.checked_in':   { Icon: CheckCircle, bg: 'rgba(70,168,249,0.12)',   color: BLUE },
  pre_visit_submitted:    { Icon: FileText,    bg: '#F1F5F9',                  color: '#64748B' },
  new_case:               { Icon: UserPlus,    bg: '#F1F5F9',                  color: NAVY },
  // Fallback
  default:                { Icon: Bell,        bg: 'rgba(70,168,249,0.12)',   color: BLUE },
}

// v220.12.2: Heuristic icon inference for unrecognized types.
// Older notifications may have been stored with arbitrary type values; if the type
// isn't in the map above, derive an icon from the title text instead of falling back
// to a generic Bell.
function inferStyleFromTitle(title) {
  if (!title) return null
  const t = title.toLowerCase()
  if (t.includes('appointment') && (t.includes('reschedul') || t.includes('modif'))) {
    return { Icon: RefreshCw, bg: 'rgba(70,168,249,0.12)', color: BLUE }
  }
  if (t.includes('appointment') && (t.includes('reject') || t.includes('cancel'))) {
    return { Icon: XCircle, bg: 'rgba(239,68,68,0.08)', color: '#EF4444' }
  }
  if (t.includes('appointment')) {
    return { Icon: Calendar, bg: 'rgba(70,168,249,0.12)', color: BLUE }
  }
  if (t.includes('message') || t.includes('reply')) {
    return { Icon: MessageSquare, bg: 'rgba(70,168,249,0.12)', color: BLUE }
  }
  if (t.includes('referral')) {
    return { Icon: UserPlus, bg: ICE_BLUE, color: BLUE }
  }
  if (t.includes('observation') || t.includes('check-in') || t.includes('check in')) {
    return { Icon: Clock, bg: 'rgba(70,168,249,0.12)', color: BLUE }
  }
  if (t.includes('case') && t.includes('reopen')) {
    return { Icon: RefreshCw, bg: '#F1F5F9', color: '#64748B' }
  }
  if (t.includes('case') && (t.includes('sent') || t.includes('shared'))) {
    return { Icon: Send, bg: '#F1F5F9', color: '#64748B' }
  }
  if (t.includes('care plan') || t.includes('diagnosis') || t.includes('finalized') || t.includes('ready')) {
    return { Icon: CheckCircle, bg: ICE_BLUE, color: NAVY }
  }
  if (t.includes('welcome')) {
    return { Icon: CheckCircle, bg: ICE_BLUE, color: BLUE }
  }
  return null
}

function getNotifStyle(notification) {
  // notification may be a string (legacy callers) or the whole object
  const type = typeof notification === 'string' ? notification : notification?.type
  if (type && NOTIF_STYLES[type]) return NOTIF_STYLES[type]
  // v220.12.2: Try title-based inference before falling back to generic Bell
  if (typeof notification === 'object' && notification?.title) {
    const inferred = inferStyleFromTitle(notification.title)
    if (inferred) return inferred
  }
  return NOTIF_STYLES.default
}

// ★ PROD-729: WHAT A ROW SAYS. The API stores every notification's title and message in English.
//   case_decline_undone's copy is an English DRAFT on the server (Release A), so under es that row
//   reads the approved generic `notifications.title` and no message; tapping it still opens what
//   getDeepLink names. (case_declined is drawn by caseDeclinedCopy.)
// ★★ PROD-729 r1 (review B-S1, 2026-10-09): ONLY case_decline_undone. The first cut applied the generic
//   row to every type not in NOTIF_STYLES, and five types today's API already sends to patients
//   (appointment_check_in, appointment_soon, case_reopened_patient, referral_unaccepted,
//   referral_reminder) lost their time and text under es. Every other type, in every language,
//   reads the server's words exactly as before PROD-729.
function rowCopy(notification, language, t) {
  // s119 (canvas "Orella Share Code Approval", en + es approved): the opener request and the "opened" notice read
  //   OUR words in every language (the server stores English); the name is the opener as the directory names them.
  const _t119 = String(notification?.type || '')
  if (_t119 === 'share_open_request' || _t119 === 'share_code_opened') {
    const d = (notification && typeof notification.data === 'object' && notification.data) || {}
    const who = clinicianFormsFor('', d.openerTitle || '', 'display', language)
    return _t119 === 'share_open_request'
      ? { title: t('shareApproval.allowTitle'), message: t('shareApproval.wantsToOpen', who) }
      : { title: t('shareApproval.notifOpenedTitle'), message: `${t('shareApproval.noticeOpened', who)}. ${t('shareApproval.noticeReadOnly')}.` }
  }
  // s111 (Dr. Rami 2026-10-19, en + es approved): the updated-summary notice reads OUR words in every
  //   language (the server stores English only); no condition names either way.
  if (String(notification?.type || '') === 'case_summary_updated') {
    return { title: t('notifications.summaryUpdated.title'), message: t('notifications.summaryUpdated.body') }
  }
  if (language === 'es' && isCaseDeclineUndone(notification)) return { title: t('notifications.title'), message: null }
  // s102 (Dr. Rami list 3, B3): appointment_rejected under es reads the approved sentence; the reason
  //   line only when the API sent its approved Spanish (rejectionReasonEs). No name -> the server's words.
  if (language === 'es' && String(notification?.type || '').replace(/\./g, '_') === 'appointment_rejected') {
    const data = (notification && typeof notification.data === 'object' && notification.data) || {}
    const doctorName = String(data.doctorName || '').trim().replace(/^Dr\.?\s+/i, '')
    const reasonEs = String(data.rejectionReasonEs || '').trim()
    if (doctorName) {
      const parts = [t('notifications.appointmentRejected.line', { ...clinicianFormsFor(data.doctorTitle, doctorName, 'display', language), doctorName })]
      if (reasonEs) parts.push(t('notifications.caseDeclined.reason', { reason: reasonEs }))
      return { title: t('notifications.title'), message: parts.join(' ') }
    }
  }
  // PROD-1137: appointment_cancelled_by_doctor under es reads our sentence and the reason sentence by
  //   its code (never free text). No name -> the server's words. en keeps the server's words (they
  //   also name the date and time). Same pattern as appointment_rejected above: our own es string,
  //   the server's messageEs stays unused.
  const cancel = language === 'es' ? physicianCancelNotice(notification) : null
  if (cancel && cancel.doctorName) {
    const reasonKey = cancelReasonKey(cancel.reasonCode)
    // The cancelled slot, like the server's own es sentence names it (L6); no date or time -> the short line.
    const when = cancel.date && cancel.time ? formatDate(cancel.date, { month: 'short', day: 'numeric' }) : ''
    const parts = [when
      ? t('notifications.appointmentCancelledByDoctor.lineWhen', { ...clinicianFormsFor(cancel.doctorTitle, cancel.doctorName, 'display', language), doctorName: cancel.doctorName, date: when, time: formatAppointmentTime(cancel.time) })
      : t('notifications.appointmentCancelledByDoctor.line', { ...clinicianFormsFor(cancel.doctorTitle, cancel.doctorName, 'display', language), doctorName: cancel.doctorName })]
    if (reasonKey) parts.push(t('caseHome.cancelReason', { reason: t(reasonKey) }))
    return { title: t('notifications.title'), message: parts.join(' ') }
  }
  return { title: notification.title, message: notification.message }
}

// Deep-link routing: notification type → target route
// v220.12.2: Handles both snake_case (current) and dot.notation (legacy pre-unification)
function getDeepLink(notification) {
  const data = notification.data || {}
  const type = notification.type || ''

  // Normalize legacy dot-notation to snake_case for routing decisions
  const normalizedType = type.replace(/\./g, '_')

  // Appointment-related → visits page
  // v226.9.1 (G1): case_accepted removed — routes to case home instead (per decision).
  if ([
    'appointment_confirmed', 'appointment_modified', 'appointment_rescheduled',
    'appointment_rejected', 'appointment_reschedule_declined', 'appointment_cancelled', // PROD-1008: restored visit -> /visits like a decline
  ].includes(normalizedType)) {
    return '/visits'
  }

  // s119: someone asks to open the case -> the patient's Allow / Deny; the "opened" notice -> the case.
  if (normalizedType === 'share_open_request') return data.caseId ? `/case/${data.caseId}/allow-access` : '/notifications'
  // s126: the "opened" notice -> the share hub's Opened-by sheet (who opened it, and Revoke while not accepted).
  if (normalizedType === 'share_code_opened') return data.caseId ? `/case/${data.caseId}?go=opened` : '/notifications'

  // PROD-1137: the physician cancelled the visit -> the case home, where "Choose a new time" (same
  //   physician) and "Cancel & withdraw" (another physician) are; /visits when the notice names no case.
  if (normalizedType === 'appointment_cancelled_by_doctor') {
    return data.caseId ? `/case/${data.caseId}` : '/visits'
  }

  // v226.9.3 (G3): check-in reminder -> Visits with the check-in flow auto-launched.
  if (normalizedType === 'appointment_check_in') {
    return data.caseId ? `/visits?openCheckIn=1&caseId=${data.caseId}` : '/visits'
  }
  // v226.9.3 (G3): room ready -> Visits with the scannable QR pass auto-opened.
  if (normalizedType === 'room_ready') {
    return data.caseId ? `/visits?openPass=1&caseId=${data.caseId}` : '/visits'
  }

  // TEST 3.18: a declined case → its case home, where the patient chooses another physician
  //   (the case is back to ready_to_share, so the home offers "Share with a doctor" again).
  // PROD-729: the physician UNDID his decline (orella-api Release A, case_decline_undone) -> the
  //   same case home, after the same store refresh (handleNotificationClick): the case is his again.
  if (normalizedType === 'case_declined' || normalizedType === 'case_decline_undone') {
    return data.caseId ? `/case/${data.caseId}` : null
  }

  // Messages → open case and auto-open chat
  if ((normalizedType === 'case_message' || normalizedType === 'case_message_to_doctor') && data.caseId) {
    return `/case/${data.caseId}?openChat=1`
  }

  // Observation reports → case detail (no chat)
  if (normalizedType === 'observation_finding_reported' && data.caseId) {
    return `/case/${data.caseId}`
  }

  // s111: an updated summary opens the case's conditions list (CaseHome ?go=conditions).
  if (normalizedType === 'case_summary_updated' && data.caseId) {
    return `/case/${data.caseId}?go=conditions`
  }

  // v226.9.2 (G2): finalized care plans land directly on Diagnose to review & complete.
  if ((normalizedType === 'case_finalized' || normalizedType === 'follow_up_ready') && data.caseId) {
    return `/case/${data.caseId}?go=diagnose`
  }

  // v226.9.4 (G4): observation check-in -> Deliver, auto-launching the symptom-check/report flow.
  if (normalizedType === 'observation_check_in' && data.caseId) {
    return `/case/${data.caseId}?openReport=1`
  }

  // Case-related → specific case
  if (data.caseId) {
    return `/case/${data.caseId}`
  }

  // No deep-link target — stay on the Notifications screen
  return null
}

// Time bucketing — Today / This week / Earlier
function bucketFor(createdAt) {
  if (!createdAt) return 'earlier'
  const d = new Date(createdAt)
  if (Number.isNaN(d.getTime())) return 'earlier'
  const now = new Date()
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  if (sameDay) return 'today'
  const diffMs = now - d
  const sevenDays = 7 * 24 * 60 * 60 * 1000
  if (diffMs < sevenDays) return 'thisWeek'
  return 'earlier'
}

function NotificationRow({ notification, onClick, onDelete }) {
  const { t, language } = useI18n()
  const isUnread = !notification.read
  const style = getNotifStyle(notification)
  const IconComp = style.Icon
  // TEST 3.18: a decline is read in the app language with the approved reason, and the whole
  //   message shows (one ellipsised line would cut the reason off).
  const declined = isCaseDeclined(notification)
  const { title: rowTitle, message: rowMessage } = declined
    ? caseDeclinedCopy(notification, language, t)
    : rowCopy(notification, language, t)

  // v220.12.3 — Swipe-to-delete state. The row translates left as the user drags.
  // Past DELETE_THRESHOLD, releasing the swipe deletes the row.
  // Past REVEAL_THRESHOLD, releasing snaps to the open state (Delete button visible).
  // Less than REVEAL_THRESHOLD snaps back to closed.
  const DELETE_THRESHOLD = 140  // px — full-commit swipe
  const REVEAL_THRESHOLD = 70   // px — reveal Delete button
  const OPEN_OFFSET = 88        // px — width of revealed Delete button

  const [offset, setOffset] = useState(0)
  const [animating, setAnimating] = useState(false)
  const [removed, setRemoved] = useState(false)
  const touchStartX = useRef(null)
  const touchStartY = useRef(null)
  const dragging = useRef(false)

  const handleTouchStart = (e) => {
    const startX = e.touches[0].clientX
    // v220.12.4 — Don't capture touches starting in the leftmost 24px;
    // iOS Safari uses that area for native back-swipe.
    if (startX < 24) {
      touchStartX.current = null
      return
    }
    touchStartX.current = startX
    touchStartY.current = e.touches[0].clientY
    dragging.current = false
    setAnimating(false)
  }

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return
    const dx = e.touches[0].clientX - touchStartX.current
    const dy = e.touches[0].clientY - touchStartY.current
    // Only treat as horizontal swipe once we cross 8px AND horizontal beats vertical
    if (!dragging.current) {
      if (Math.abs(dx) < 8) return
      if (Math.abs(dx) <= Math.abs(dy)) return  // vertical scroll wins
      dragging.current = true
    }
    // Constrain to left-swipe (negative dx); allow tiny right rebound from open state
    const next = Math.min(0, Math.max(-DELETE_THRESHOLD - 20, dx))
    setOffset(next)
  }

  const handleTouchEnd = () => {
    touchStartX.current = null
    touchStartY.current = null
    if (!dragging.current) {
      setOffset(0)
      return
    }
    dragging.current = false
    setAnimating(true)
    if (offset <= -DELETE_THRESHOLD) {
      // Commit delete — animate fully off screen then call onDelete
      setOffset(-window.innerWidth)
      setTimeout(() => {
        setRemoved(true)
        onDelete?.(notification)
      }, 180)
    } else if (offset <= -REVEAL_THRESHOLD) {
      setOffset(-OPEN_OFFSET)
    } else {
      setOffset(0)
    }
  }

  const handleDeleteButton = (e) => {
    e.stopPropagation()
    setAnimating(true)
    setOffset(-window.innerWidth)
    setTimeout(() => {
      setRemoved(true)
      onDelete?.(notification)
    }, 180)
  }

  const handleRowClick = () => {
    // If swipe revealed the delete button, first tap closes it instead of triggering navigation
    if (offset < 0) {
      setAnimating(true)
      setOffset(0)
      return
    }
    onClick?.()
  }

  if (removed) return null

  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      // v220.12.5 — Each row is its own discrete card.
      // Outer wrapper holds the swipe-reveal Delete bar; card itself is wrapped in margin.
      margin: '0 12px 8px',
      borderRadius: 14,
    }}>
      {/* Swipe-reveal Delete bar (only visible when swiped) */}
      <button
        onClick={handleDeleteButton}
        aria-label="Delete notification"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 88,
          background: '#EF4444',
          color: '#fff',
          border: 'none',
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        <Trash2 size={18} strokeWidth={2} />
        {t('common.delete', 'Delete')}
      </button>

      {/* Foreground card */}
      <div
        onClick={handleRowClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick() } }}
        style={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 14px',
          cursor: 'pointer',
          background: '#FFFFFF',
          border: `1px solid ${isUnread ? '#B9DCFB' : '#EEF2F7'}`,
          borderRadius: 14,
          transform: `translateX(${offset}px)`,
          transition: animating ? 'transform 0.18s ease, background 0.15s' : 'background 0.15s',
          position: 'relative',
          zIndex: 1,
          touchAction: 'pan-y',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          boxSizing: 'border-box',
        }}
      >
        {/* Icon tile */}
        <div
          style={{
            width: 36, height: 36, borderRadius: 10,
            background: style.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconComp size={17} color={style.color} strokeWidth={2} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            fontSize: 13.5,
            fontWeight: isUnread ? 600 : 500,
            color: isUnread ? NAVY : '#64748B',
            margin: 0,
            lineHeight: 1.3,
          }}>
            {rowTitle}
          </p>
          {rowMessage && (
            <p style={{
              fontSize: 11.5,
              color: isUnread ? '#64748B' : '#94A3B8',
              margin: '2px 0 0',
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: declined ? 'normal' : 'nowrap',
            }}>
              {rowMessage}
            </p>
          )}
          <span style={{
            fontSize: 10,
            color: '#94A3B8',
            marginTop: 3,
            display: 'block',
          }}>
            {notification.timeAgo || formatTimeAgo(notification.created_at)}
          </span>
        </div>

        {/* Right edge: unread dot (top) + trash button (bottom) */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
          paddingLeft: 4,
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: 999,
            background: isUnread ? BLUE : 'transparent',
          }} />
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteButton(e) }}
            aria-label="Delete notification"
            style={{
              width: 32, height: 32, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#CBD5E1',
              transition: 'all 0.15s',
              padding: 0,
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#CBD5E1'; e.currentTarget.style.background = 'transparent' }}
            onTouchStart={(e) => { e.stopPropagation() }}
          >
            <Trash2 size={15} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  )
}

function SectionHeader({ label, count }) {
  return (
    <p style={{
      fontSize: 9,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      color: '#94A3B8',
      padding: '14px 20px 8px',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    }}>
      {label}
      {count > 0 && (
        <span style={{
          width: 18, height: 18, borderRadius: 999,
          background: BLUE, color: '#fff',
          fontSize: 9, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{count}</span>
      )}
    </p>
  )
}

export default function NotificationsScreen() {
  const navigate = useNavigate()
  const goBack = useMenuBack()
  const { t } = useI18n()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount)

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true)
      const result = await notificationsAPI.list()
      setNotifications(result.notifications || [])
    } catch (err) {
      console.error('Failed to load notifications:', err)
      setError(t('errors2.failedToLoadNotifications'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadNotifications()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadNotifications()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [loadNotifications])

  const unreadCount = notifications.filter(n => !n.read).length

  // Group by time bucket
  const buckets = useMemo(() => {
    const today = []
    const thisWeek = []
    const earlier = []
    for (const n of notifications) {
      const b = bucketFor(n.created_at)
      if (b === 'today') today.push(n)
      else if (b === 'thisWeek') thisWeek.push(n)
      else earlier.push(n)
    }
    return { today, thisWeek, earlier }
  }, [notifications])

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead()
      setNotifications(notifications.map(n => ({ ...n, read: true })))
      useNotificationStore.getState().clearUnread()
    } catch (err) {
      console.error('Failed to mark all read:', err)
    }
  }

  // v220.12.3 — Delete a single notification (called from swipe-to-delete)
  const handleDeleteNotification = async (notification) => {
    // Optimistic UI: remove from list immediately
    const prev = notifications
    setNotifications(notifications.filter(n => n.id !== notification.id))
    if (!notification.read) {
      useNotificationStore.getState().decrementUnread()
    }
    try {
      await notificationsAPI.delete(notification.id)
    } catch (err) {
      console.error('Failed to delete notification:', err)
      // Roll back on failure
      setNotifications(prev)
      // Best-effort: re-fetch to resync
      loadNotifications()
    }
  }

  // v220.12.3 — Clear all notifications (with confirm)
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false)
  const [clearing, setClearing] = useState(false)

  const handleClearAll = async () => {
    setClearing(true)
    const prev = notifications
    setNotifications([])  // optimistic
    useNotificationStore.getState().clearUnread()
    try {
      await notificationsAPI.clearAll()
      setClearConfirmOpen(false)
    } catch (err) {
      console.error('Failed to clear all notifications:', err)
      setNotifications(prev)  // rollback
      // Re-derive unread
      useNotificationStore.getState().fetchUnreadCount?.()
    } finally {
      setClearing(false)
    }
  }

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      try {
        await notificationsAPI.markRead(notification.id)
        setNotifications(notifications.map(n =>
          n.id === notification.id ? { ...n, read: true } : n
        ))
        useNotificationStore.getState().decrementUnread()
      } catch (err) {
        console.error('Failed to mark read:', err)
      }
    }
    const deepLink = getDeepLink(notification)
    if (!deepLink) return

    // v220.12.2: If the deep-link points to a specific case, verify the case
    // exists in the user's store before navigating. Stale notifications can
    // reference deleted or revoked cases — sending the user to /case/{stale-id}
    // bounces them through the case loader and back to "/" (landing), which
    // looks like a crash. Fall back to /cases list instead.
    const caseMatch = deepLink.match(/^\/case\/([^/?]+)/)
    if (caseMatch) {
      const targetCaseId = caseMatch[1]
      const inStore = () => (useCaseStore.getState().cases || [])
        .some(c => c.id === targetCaseId || c.caseId === targetCaseId)

      // v226.9.1 (G1): a freshly-spawned case (e.g. a referral's Case B) may not be
      // in the local store yet. Refetch once before deciding anything.
      // TEST 3.18: a declined case IS in the store, but as it was before the decline (sent to a
      //   physician); refresh first so the case home opens on the released case.
      // PROD-729: and an UNDONE decline is in the store as the released case (ready_to_share, no
      //   physician); refresh so the case home opens on the case that is with the physician again.
      // PROD-1137: a physician cancel likewise: the store holds the case as it was (visit confirmed).
      // s111: an updated summary likewise: the store holds the list from before the refresh.
      //   (Its own block: the gates of the arms below pin that line's exact shape.)
      if (notification.type === 'case_summary_updated') {
        try { await useCaseStore.getState().loadCases() }
        catch (err) { console.warn('[Notifications] summary refetch failed:', err?.name) }
      }
      if (!inStore() || !!physicianCancelNotice(notification) || isCaseDeclined(notification) || isCaseDeclineUndone(notification)) {
        try { await useCaseStore.getState().loadCases() }
        catch (err) { console.warn('[Notifications] loadCases refetch failed:', err) }
      }

      if (!inStore()) {
        const nt = (notification.type || '').replace(/\./g, '_')
        const isReferral = ['referral_created', 'referral_accepted', 'referral_unaccepted'].includes(nt)
        if (isReferral) {
          // Per decision: never bounce a referral to the cases list. If Case B is still
          // unresolvable (e.g. external referral, no spawned case), no-op.
          console.warn(`[Notifications] Referral case ${targetCaseId} unresolved; staying put.`)
          return
        }
        console.warn(`[Notifications] Case ${targetCaseId} not in store, falling back to /cases`)
        navigate('/cases')
        return
      }
    }

    navigate(deepLink)
  }

  const isEmpty = notifications.length === 0

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-2.5">
        <button
          onClick={() => goBack()}
          className="p-0"
          style={{ background: 'none', border: 'none' }}
        >
          <ArrowLeft size={18} color="#64748B" strokeWidth={2.5} />
        </button>
        <h1 className="flex-1" style={{ fontSize: 17, fontWeight: 700, color: NAVY, margin: 0 }}>
          {t('notifications.title')}
        </h1>
        {notifications.length > 0 && (
          <button
            onClick={() => setClearConfirmOpen(true)}
            style={{ fontSize: 11, fontWeight: 600, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('notifications.clearAll') || 'Clear all'}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-8 overflow-auto">
        {/* s101: What's new is the first notification, not a pop-up (WhatsNewNotice). */}
        <WhatsNewNotice />
        {loading ? (
          <div className="mt-16 flex flex-col items-center justify-center">
            <OrellaSpinner size={32} />
            <p className="text-gray-500 mt-3 text-sm">{t('settingsMisc.loadingNotifications')}</p>
          </div>
        ) : error ? (
          <div className="mt-16 mx-4 bg-red-50 rounded-2xl p-8 text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="font-medium mt-4"
              style={{ color: BLUE, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {t('common.tryAgain')}
            </button>
          </div>
        ) : isEmpty ? (
          /* Empty State — refined centered (v228.2.1) */
          <div className="mt-20 px-8 flex flex-col items-center text-center">
            <div
              className="flex items-center justify-center mb-5"
              style={{ width: 84, height: 84, borderRadius: 22, background: ICE_BLUE, border: '1.5px solid #A7D9FC' }}
            >
              <Bell size={40} strokeWidth={1.7} color={BLUE} />
            </div>
            <h3 style={{ fontSize: 19, fontWeight: 800, color: NAVY, margin: 0 }}>
              {t('settingsMisc.allCaughtUp')}
            </h3>
            <p style={{ fontSize: 14, color: '#64748B', margin: '8px 0 0', lineHeight: 1.5, maxWidth: 260 }}>
              {t('notifications.wellLetYouKnow')}
            </p>
            <button
              onClick={() => navigate('/notification-preferences')}
              className="flex items-center justify-center gap-2"
              style={{
                marginTop: 28, height: 46, padding: '0 20px', borderRadius: 999,
                border: '1.5px solid #D8E6F0', background: '#fff', color: NAVY,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              <Settings size={16} color={NAVY} />
              {t('notifications.managePreferences')}
            </button>
          </div>
        ) : (
          <>
            {buckets.today.length > 0 && (
              <>
                <SectionHeader
                  label={t('notifications.bucketToday') || 'Today'}
                  count={buckets.today.filter(n => !n.read).length}
                />
                <div style={{ background: '#F6F8FB', padding: '8px 0 4px' }}>
                  {buckets.today.map((n) => (
                    <NotificationRow key={n.id} notification={n} onClick={() => handleNotificationClick(n)} onDelete={handleDeleteNotification} />
                  ))}
                </div>
              </>
            )}
            {buckets.thisWeek.length > 0 && (
              <>
                <SectionHeader
                  label={t('notifications.bucketThisWeek') || 'This week'}
                  count={buckets.thisWeek.filter(n => !n.read).length}
                />
                <div style={{ background: '#F6F8FB', padding: '8px 0 4px' }}>
                  {buckets.thisWeek.map((n) => (
                    <NotificationRow key={n.id} notification={n} onClick={() => handleNotificationClick(n)} onDelete={handleDeleteNotification} />
                  ))}
                </div>
              </>
            )}
            {buckets.earlier.length > 0 && (
              <>
                <SectionHeader
                  label={t('notifications.bucketEarlier') || t('notifications.earlier') || 'Earlier'}
                  count={buckets.earlier.filter(n => !n.read).length}
                />
                <div style={{ background: '#F6F8FB', padding: '8px 0 4px' }}>
                  {buckets.earlier.map((n) => (
                    <NotificationRow key={n.id} notification={n} onClick={() => handleNotificationClick(n)} onDelete={handleDeleteNotification} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Settings link (hidden on empty state — the empty state has its own preferences pill) */}
        {!loading && !error && !isEmpty && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => navigate('/notification-preferences')}
              className="flex items-center gap-1.5"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <Settings size={12} color={BLUE} />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: BLUE }}>
                {t('notifications.managePreferences')}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* v220.12.3 — Clear All confirmation modal */}
      {clearConfirmOpen && (
        <div
          onClick={() => !clearing && setClearConfirmOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            background: 'rgba(15,36,56,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              width: '100%',
              maxWidth: 340,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '22px 22px 6px', textAlign: 'center' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 999,
                background: 'rgba(239,68,68,0.12)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 12,
              }}>
                <AlertTriangle size={20} color="#EF4444" strokeWidth={2} />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 6px' }}>
                {t('notifications.clearAllConfirmTitle') || 'Clear all notifications?'}
              </h3>
              <p style={{ fontSize: 13, color: '#64748B', margin: 0, lineHeight: 1.5 }}>
                {t('notifications.clearAllConfirmBody') || 'This permanently removes all your notifications. You won\'t be able to undo this.'}
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: 8,
              padding: '16px 18px 18px',
            }}>
              <button
                onClick={() => setClearConfirmOpen(false)}
                disabled={clearing}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 10,
                  border: '1px solid #E2E8F0',
                  background: '#fff',
                  color: NAVY,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: clearing ? 'default' : 'pointer',
                  opacity: clearing ? 0.5 : 1,
                }}
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleClearAll}
                disabled={clearing}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 10,
                  border: 'none',
                  background: '#EF4444',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: clearing ? 'default' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                {clearing && <OrellaSpinner size={16} tone="inherit" />}
                {clearing ? (t('notifications.clearing') || 'Clearing…') : (t('notifications.clearAll') || 'Clear all')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
